import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

export interface SelectOption {
  value: string;
  label: string;
  index?: number;
}

@Component({
  standalone: true,
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.css'],
  imports: [CommonModule, TranslateModule],
})
export class SelectComponent implements OnDestroy {
  @Input() id!: string;
  @Output() handleSelect = new EventEmitter<SelectOption>();
  @Input() options: SelectOption[];

  @ViewChild('menuContainer') menuContainer: ElementRef;
  @ViewChild('trigger') trigger: ElementRef;

  isActive: boolean;
  positionClass = 'bottom-left';

  private globalClickListener: () => void;

  constructor(private elementRef: ElementRef, private renderer: Renderer2) {}

  ngOnDestroy(): void {
    this.removeClickListener();
  }

  onChange(event): void {
    this.isActive = false;
    this.handleSelect.emit(event);
  }

  toggleDropdown(): void {
    this.isActive = !this.isActive;
    if (this.isActive) {
      this.setPosition();
      this.globalClickListener = this.renderer.listen(
        'document',
        'click',
        (event: Event) => {
          this.closeDropdown(event);
        }
      );
    } else {
      this.removeClickListener();
    }
  }

  setPosition() {
    // Use the max-height value here
    const menuHeight = 200;
    const menuWidth = this.menuContainer.nativeElement.offsetWidth;

    const rect = this.trigger.nativeElement.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = window.innerWidth - rect.right;

    // Determine possible positions based on available space for each corner
    const canFitBottomLeft = spaceBelow >= menuHeight && spaceLeft >= menuWidth;
    const canFitBottomRight =
      spaceBelow >= menuHeight && spaceRight >= menuWidth;
    const canFitTopLeft = spaceAbove >= menuHeight && spaceLeft >= menuWidth;
    const canFitTopRight = spaceAbove >= menuHeight && spaceRight >= menuWidth;

    // Choose the best available position
    if (canFitBottomLeft) {
      this.positionClass = 'bottom-left';
    } else if (canFitBottomRight) {
      this.positionClass = 'bottom-right';
    } else if (canFitTopLeft) {
      this.positionClass = 'top-left';
    } else if (canFitTopRight) {
      this.positionClass = 'top-right';
    } else {
      // Fallback to a default, e.g., bottom-left if no ideal space is available
      this.positionClass = 'bottom-left';
    }
  }

  closeDropdown(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isActive = false;
      this.removeClickListener();
    }
  }

  private removeClickListener() {
    if (this.globalClickListener) {
      this.globalClickListener();
      this.globalClickListener = null;
    }
  }
}
